import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import {
    SACRAMENTO_COUNTY_CITIES,
    PLACER_COUNTY_CITIES,
} from "@/data/content";

export default function ServiceAreas() {
    const schema = {
        "@context": "https://schema.org",
        "@type": "Attorney",
        name: "Joseph G. Helfrick",
        areaServed: [
            {
                "@type": "AdministrativeArea",
                name: "Sacramento County, California",
                containsPlace: SACRAMENTO_COUNTY_CITIES.map((c) => ({
                    "@type": "City",
                    name: c,
                })),
            },
            {
                "@type": "AdministrativeArea",
                name: "Placer County, California",
                containsPlace: PLACER_COUNTY_CITIES.map((c) => ({
                    "@type": "City",
                    name: c,
                })),
            },
        ],
    };

    return (
        <>
            <SEO
                title="Service Areas — Sacramento, CA, and surrounding areas"
                description="Joseph G. Helfrick represents personal injury clients throughout Sacramento, CA, and surrounding areas — Sacramento, Elk Grove, Folsom, Citrus Heights, Rancho Cordova, Roseville, Rocklin, Lincoln, Auburn, Granite Bay, Loomis, Tahoe City, and the surrounding Sierra communities."
                path="/service-areas"
                schema={schema}
                breadcrumbs={[
                    { name: "Home", path: "/" },
                    { name: "Service Areas", path: "/service-areas" },
                ]}
            />

            {/* HERO */}
            <section
                data-testid="areas-hero"
                className="border-b border-[#1F3458]"
            >
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-16 lg:pt-28 lg:pb-24 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-8">
                        <div className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] font-bold">
                            Service Areas
                        </div>
                        <h1 className="mt-6 font-serif text-5xl lg:text-7xl text-[#F7F5EB] leading-[0.95] font-light">
                            Sacramento County.
                            <br />
                            <span className="italic text-[#D4AF37]">
                                Placer County.
                            </span>
                            <br />
                            All the towns in between.
                        </h1>
                        <p className="mt-8 text-lg text-[#9FB3D4] max-w-3xl leading-relaxed">
                            From the Capitol downtown to the Sierra foothills,
                            Joe represents clients across the cities and
                            communities of Northern California. If you were hurt
                            here, we can help here.
                        </p>
                        <div
                            className="tldr mt-8 max-w-3xl border-l-2 border-[#D4AF37] pl-5 text-[#F7F5EB]/85 text-sm leading-relaxed"
                            data-speakable
                        >
                            <span className="block text-[#D4AF37] text-[10px] uppercase tracking-[0.28em] font-bold mb-2">
                                Quick Answer
                            </span>
                            Joe Helfrick represents personal injury clients in
                            Sacramento County (Sacramento, Elk Grove, Folsom,
                            Citrus Heights, Rancho Cordova, Carmichael, Fair
                            Oaks, Galt) and Placer County (Roseville, Rocklin,
                            Lincoln, Auburn, Loomis, Granite Bay, Colfax, and
                            into the Tahoe / Sierra region).
                        </div>
                    </div>
                </div>
            </section>

            {/* COUNTIES */}
            <section className="border-b border-[#1F3458]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 grid lg:grid-cols-2 gap-12">
                    {[
                        {
                            name: "Sacramento County",
                            blurb:
                                "From downtown Sacramento and East Sac to Elk Grove, Folsom, Rancho Cordova, Citrus Heights, and the unincorporated communities along the river — we work cases throughout every corner of the county.",
                            cities: SACRAMENTO_COUNTY_CITIES,
                            testid: "areas-sacramento",
                        },
                        {
                            name: "Placer County",
                            blurb:
                                "Up Highway 80 — Roseville, Rocklin, Lincoln, Loomis, Granite Bay, Auburn, and into the Sierra near Foresthill, Colfax, Truckee, and the Tahoe basin. Mountain cases come with their own playbook, and we know it.",
                            cities: PLACER_COUNTY_CITIES,
                            testid: "areas-placer",
                        },
                    ].map((county) => (
                        <div
                            key={county.name}
                            data-testid={county.testid}
                            className="bg-[#11203D] border border-[#1F3458] p-8 md:p-12"
                        >
                            <MapPin size={28} className="text-[#D4AF37]" />
                            <h2 className="mt-4 font-serif text-4xl text-[#F7F5EB] leading-tight">
                                {county.name}
                            </h2>
                            <p className="mt-4 text-[#9FB3D4] leading-relaxed">
                                {county.blurb}
                            </p>
                            <div className="mt-8 hairline-gold" />
                            <div className="mt-6 text-[#D4AF37] text-[11px] uppercase tracking-[0.24em] font-bold">
                                Cities & Communities Served
                            </div>
                            <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-[#F7F5EB]/90">
                                {county.cities.map((c) => (
                                    <li
                                        key={c}
                                        className="flex items-center gap-2"
                                    >
                                        <span className="h-1 w-1 bg-[#D4AF37]" />
                                        {c}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section data-testid="areas-cta">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 grid lg:grid-cols-12 gap-12 items-end">
                    <div className="lg:col-span-7">
                        <h2 className="font-serif text-4xl lg:text-6xl text-[#F7F5EB] leading-tight">
                            Not sure if you're{" "}
                            <span className="italic text-[#D4AF37]">
                                in our area?
                            </span>
                        </h2>
                        <p className="mt-6 text-[#9FB3D4] text-lg max-w-2xl">
                            Call or message us anyway. If we're not the right
                            firm for your matter, we'll tell you — and point you
                            to someone who is.
                        </p>
                    </div>
                    <div className="lg:col-span-5">
                        <Link
                            to="/contact"
                            data-testid="areas-cta-link"
                            className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#07142B] font-bold uppercase tracking-wider text-sm px-8 py-4 hover:bg-[#B8962A] transition-colors"
                        >
                            Contact Joe <ArrowRight size={16} />
                        </Link>
                        <div className="mt-5">
                            <Link
                                to="/case-evaluator"
                                data-testid="areas-cta-evaluator"
                                className="text-[#9FB3D4] text-sm underline underline-offset-4 decoration-[#1F3458] hover:text-[#D4AF37] hover:decoration-[#D4AF37] transition-colors"
                            >
                                Prefer to start online? Take the 60-second
                                case evaluator →
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
